/** localStorage wrapper: every access is guarded (private mode, blocked storage, sandboxed iframes). */
export interface PersistedState {
  session_id?: string;
  conversation_id?: string;
  /** Cached tenant theme so the launcher can be styled before /v1/session is called. */
  theme?: Record<string, unknown>;
  /** Tenant locale from the last `/v1/session` (sent as `locale` before the tenant is known). */
  tenant_locale?: string;
  /** True once the visitor left an email for this tenant (only the flag is stored, never the address). */
  contact_known?: boolean;
  /** When the visitor closed the launcher bubble (it stays away for a while). */
  teaser_dismissed_at?: number;
}

const PREFIX = 'aicommerce:';

export function storageKey(tenantKey: string): string {
  return `${PREFIX}${tenantKey}`;
}

export function loadState(tenantKey: string): PersistedState {
  try {
    const raw = window.localStorage.getItem(storageKey(tenantKey));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as PersistedState) : {};
  } catch {
    return {};
  }
}

export function saveState(tenantKey: string, patch: PersistedState): void {
  try {
    const next = { ...loadState(tenantKey), ...patch };
    window.localStorage.setItem(storageKey(tenantKey), JSON.stringify(next));
  } catch {
    /* storage unavailable: the widget keeps working without persistence */
  }
}

export function clearState(tenantKey: string, keys: (keyof PersistedState)[]): void {
  try {
    const next = loadState(tenantKey);
    for (const k of keys) delete next[k];
    window.localStorage.setItem(storageKey(tenantKey), JSON.stringify(next));
  } catch {
    /* ignore */
  }
}
